import { Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { marked } from 'marked';
import TurndownService from 'turndown';

export interface ConversionOptions {
  headingStyle: 'setext' | 'atx';
  bulletListMarker: '-' | '*' | '+';
  codeBlockStyle: 'indented' | 'fenced';
  emDelimiter: '_' | '*';
  strongDelimiter: '**' | '__';
  linkStyle: 'inlined' | 'referenced';
}

@Injectable({
  providedIn: 'root',
})
export class MarkdownConverterService {
  private turndownService!: TurndownService;
  private defaultOptions: ConversionOptions = {
    headingStyle: 'atx',
    bulletListMarker: '-',
    codeBlockStyle: 'fenced',
    emDelimiter: '_',
    strongDelimiter: '**',
    linkStyle: 'inlined',
  };

  constructor(private domSanitizer: DomSanitizer) {
    this.configureConverter();
  }

  /**
   * Convert HTML content to markdown format
   * @param html - HTML string to convert
   * @returns Markdown string
   */
  htmlToMarkdown(html: string): string {
    try {
      if (!html || html.trim() === '') {
        return '';
      }

      // Sanitize HTML using Angular's DomSanitizer
      const sanitizedHtml = this.domSanitizer.sanitize(SecurityContext.HTML, html) || '';

      // Convert to markdown
      const markdown = this.turndownService.turndown(sanitizedHtml);

      // Clean up the markdown output
      return MarkdownConverterService.cleanMarkdown(markdown);
    } catch (error) {
      console.error('Error converting HTML to markdown:', error);

      // Fallback to plain text
      return MarkdownConverterService.htmlToPlainText(html);
    }
  }

  /**
   * Convert markdown content to HTML format
   * @param markdown - Markdown string to convert
   * @returns HTML string
   */
  markdownToHtml(markdown: string): string {
    try {
      if (!markdown || markdown.trim() === '') {
        return '';
      }

      // Configure marked options for security and consistency
      marked.setOptions({
        breaks: true,
        gfm: true,
        sanitize: false, // We'll handle sanitization with Angular's DomSanitizer
        smartLists: true,
        smartypants: false,
      });

      // Convert markdown to HTML
      const html = marked(markdown);

      // Sanitize the output HTML using Angular's DomSanitizer
      return this.domSanitizer.sanitize(SecurityContext.HTML, html) || '';
    } catch (error) {
      console.error('Error converting markdown to HTML:', error);

      // Fallback to escaped plain text
      return MarkdownConverterService.escapeHtml(markdown);
    }
  }

  /**
   * Configure the turndown service with conversion options
   */
  configureConverter(options?: Partial<ConversionOptions>): void {
    const config = { ...this.defaultOptions, ...options };

    this.turndownService = new TurndownService({
      headingStyle: config.headingStyle,
      bulletListMarker: config.bulletListMarker,
      codeBlockStyle: config.codeBlockStyle,
      emDelimiter: config.emDelimiter,
      strongDelimiter: config.strongDelimiter,
      linkStyle: config.linkStyle,
    });

    // Add custom rules for better conversion
    this.addCustomRules();
  }

  /**
   * Add custom conversion rules to turndown service
   */
  private addCustomRules(): void {
    // Handle line breaks
    this.turndownService.addRule('lineBreak', {
      filter: 'br',
      replacement: () => {
        return '\n';
      },
    });

    // Handle horizontal rules
    this.turndownService.addRule('horizontalRule', {
      filter: 'hr',
      replacement: () => {
        return '\n\n---\n\n';
      },
    });

    // Handle underline tags (preserve as HTML since markdown doesn't support underline)
    this.turndownService.addRule('underline', {
      filter: 'u',
      replacement: (content: string) => {
        return `<u>${content}</u>`;
      },
    });

    // Handle code blocks with language detection
    this.turndownService.addRule('codeBlock', {
      filter: (node: unknown) => {
        const element = node as HTMLElement;

        return !!(element.nodeName === 'PRE' && element.firstChild && element.firstChild.nodeName === 'CODE');
      },
      replacement: (content: string, node: unknown) => {
        const codeElement = (node as HTMLElement).firstChild as HTMLElement;
        const language = MarkdownConverterService.extractLanguageFromClass(codeElement.className);

        return `\n\n\`\`\`${language}\n${content}\n\`\`\`\n\n`;
      },
    });
  }

  /**
   * Clean up markdown output
   */
  private static cleanMarkdown(markdown: string): string {
    return markdown
      .replace(/\n{3,}/g, '\n\n') // Remove excessive line breaks
      .replace(/^\s+|\s+$/g, '') // Trim whitespace
      .replace(/\\\*/g, '*') // Fix escaped asterisks
      .replace(/\\_/g, '_'); // Fix escaped underscores
  }

  /**
   * Extract language from CSS class name
   */
  private static extractLanguageFromClass(className: string): string {
    const match = className.match(/language-(\w+)/);

    return match ? match[1] : '';
  }

  /**
   * Convert HTML to plain text as fallback
   */
  private static htmlToPlainText(html: string): string {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    return tempDiv.textContent || tempDiv.innerText || '';
  }

  /**
   * Escape HTML characters
   */
  private static escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;

    return div.innerHTML;
  }
}
